import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Tooltip } from '../components/ui/Tooltip';
import { Plus, Filter, Download, Eye, Edit, Search, MapPin, Building, X, ChevronRight, ChevronLeft, Save, CheckCircle2, AlertTriangle, Trash2, AlertCircle, List, LayoutGrid } from 'lucide-react';
import { useRBAC } from '../contexts/RBACContext';
import { PermissionGate } from '../components/PermissionGate';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { MultiSelect } from '../components/ui/MultiSelect';
import { Textarea } from '../components/ui/Textarea';
import { useNavigate } from 'react-router-dom';
import { mockData } from '../services/mockData';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data service would be imported
const initialClientsData = mockData.getClients();
const columnsConfig = [{
  key: 'number',
  label: 'Codice'
}, {
  key: 'name',
  label: 'Ragione sociale'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'zone',
  label: 'Zona'
}, {
  key: 'clientOf',
  label: 'Cliente di'
}, {
  key: 'safetyReferent',
  label: 'Ref. sicurezza'
}, {
  key: 'employeesCount',
  label: 'Dipendenti'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function ClientsPage() {
  const navigate = useNavigate();
  const {
    canCreate,
    canEdit,
    canDelete,
    currentUser
  } = useRBAC();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<any>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [viewMode, setViewMode] = useState<'principale' | 'pending'>('principale');
  // Wizard State
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<any>({});
  // Hooks
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialClientsData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult,
    isSearching
  } = useSearch(filterResult, ['name', 'number', 'email', 'vatNumber']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleEdit = (client: any) => {
    setEditingClient(client);
    setFormData(client);
    setCurrentStep(1);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setEditingClient(null);
    setFormData({
      status: 'prospect',
      clientOf: [],
      zone: '',
      addresses: [],
      number: '' // Will be pre-filled by ProgressiveNumberInput
    });
    setCurrentStep(1);
    setIsModalOpen(true);
  };
  const handleSave = () => {
    // Logic to save client
    console.log('Saving client:', formData);
    setSaveSuccess(true);
    // Show success animation then close
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  const handleDeleteClick = (e: React.MouseEvent, client: any) => {
    e.stopPropagation();
    setClientToDelete(client);
    setShowDeleteDialog(true);
  };
  const handleConfirmDelete = () => {
    console.log('Deleting client:', clientToDelete);
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setClientToDelete(null);
    }, 1500);
  };
  const hasActiveFilters = Object.keys(filters).some(key => filters[key]);
  const renderWizardStep = () => {
    switch (currentStep) {
      case 1:
        // Dati Principali
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2376">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2377">
              <Input label="Denominazione / Ragione sociale" placeholder="Es. Azienda SRL" value={formData.name || ''} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} required className="col-span-full" data-id="element-2378" />

              <ProgressiveNumberInput type="client" label="Numero cliente" value={formData.number} onChange={val => setFormData({
              ...formData,
              number: val
            })} disabled={false} data-id="element-2379" />

              <div className="space-y-1" data-id="element-2380">
                <Select label="Stato anagrafica" value={formData.status || 'prospect'} onChange={e => setFormData({
                ...formData,
                status: e.target.value
              })} options={[{
                value: 'prospect',
                label: 'Prospect'
              }, {
                value: 'active',
                label: 'Attivo'
              }, {
                value: 'inactive',
                label: 'Non attivo'
              }, {
                value: 'draft',
                label: 'Bozza'
              }]} required disabled={!canEdit('clients', 'status')} data-id="element-2381" />
                {formData.status === 'inactive' && <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200 flex items-center gap-1" data-id="element-2382">
                    <AlertTriangle className="h-3 w-3" data-id="element-2383" />
                    Attenzione: Automazioni disabilitate
                  </div>}
              </div>

              <div className="space-y-2" data-id="element-2384">
                <label className="block text-sm font-medium text-slate-700" data-id="element-2385">
                  Cliente di
                </label>
                <div className="flex items-center gap-6 pt-2" data-id="element-2386">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-2387">
                    <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={(formData.clientOf || []).includes('sef_studio')} onChange={e => {
                    const current = formData.clientOf || [];
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        clientOf: [...current, 'sef_studio']
                      });
                    } else {
                      setFormData({
                        ...formData,
                        clientOf: current.filter((v: string) => v !== 'sef_studio')
                      });
                    }
                  }} data-id="element-2388" />
                    <span className="text-sm text-slate-700" data-id="element-2389">Sef Studio</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-2390">
                    <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={(formData.clientOf || []).includes('seform')} onChange={e => {
                    const current = formData.clientOf || [];
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        clientOf: [...current, 'seform']
                      });
                    } else {
                      setFormData({
                        ...formData,
                        clientOf: current.filter((v: string) => v !== 'seform')
                      });
                    }
                  }} data-id="element-2391" />
                    <span className="text-sm text-slate-700" data-id="element-2392">Seform</span>
                  </label>
                </div>
              </div>
            </div>
          </div>;
      case 2:
        // Indirizzi
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2393">
            <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider border-b border-slate-100 pb-2" data-id="element-2394">
              Indirizzo operativo
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2395">
              <Input label="Via" value={formData.address?.street || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                street: e.target.value
              }
            })} className="col-span-full" data-id="element-2396" />
              <Input label="Civico" value={formData.address?.number || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                number: e.target.value
              }
            })} data-id="element-2397" />
              <Input label="CAP" value={formData.address?.zip || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                zip: e.target.value
              }
            })} data-id="element-2398" />
              <Input label="Comune" value={formData.address?.city || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                city: e.target.value
              }
            })} data-id="element-2399" />
              <Input label="Provincia (2 lettere)" value={formData.address?.province || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                province: e.target.value
              }
            })} maxLength={2} data-id="element-2400" />
            </div>

            <Select label="Zona" value={formData.zone || ''} onChange={e => setFormData({
            ...formData,
            zone: e.target.value
          })} options={[{
            value: 'arezzo',
            label: 'Arezzo'
          }, {
            value: 'valdarno',
            label: 'Valdarno'
          }, {
            value: 'casentino',
            label: 'Casentino'
          }, {
            value: 'valdichiana',
            label: 'Valdichiana'
          }, {
            value: 'valtiberina',
            label: 'Valtiberina'
          }, {
            value: 'firenze',
            label: 'Firenze'
          }, {
            value: 'siena',
            label: 'Siena'
          }, {
            value: 'perugia',
            label: 'Perugia'
          }]} required data-id="element-2401" />

            <div className="flex items-center gap-2 py-2" data-id="element-2402">
              <input type="checkbox" id="sameAddress" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.sameLegalAddress} onChange={e => setFormData({
              ...formData,
              sameLegalAddress: e.target.checked
            })} data-id="element-2403" />
              <label htmlFor="sameAddress" className="text-sm text-slate-700" data-id="element-2404">
                Sede legale uguale a indirizzo operativo
              </label>
            </div>

            {!formData.sameLegalAddress && <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-2405">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4" data-id="element-2406">
                  Sede legale
                </h4>
                <Textarea label="Indirizzo completo" value={formData.legalAddress || ''} onChange={e => setFormData({
              ...formData,
              legalAddress: e.target.value
            })} data-id="element-2407" />
              </div>}
          </div>;
      case 3:
        // Contatti
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2408">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2409">
              <Input label="Telefono aziendale" type="tel" value={formData.phone || ''} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} data-id="element-2410" />
              <Input label="Email amministrativa" type="email" value={formData.emailAdmin || ''} onChange={e => setFormData({
              ...formData,
              emailAdmin: e.target.value
            })} required data-id="element-2411" />
              <Input label="Email consulenza" type="email" value={formData.emailConsulting || ''} onChange={e => setFormData({
              ...formData,
              emailConsulting: e.target.value
            })} data-id="element-2412" />
              <Input label="Email formazione" type="email" value={formData.emailTraining || ''} onChange={e => setFormData({
              ...formData,
              emailTraining: e.target.value
            })} data-id="element-2413" />
              <Input label="PEC" type="email" value={formData.pec || ''} onChange={e => setFormData({
              ...formData,
              pec: e.target.value
            })} data-id="element-2414" />
              <Input label="Sito internet" type="url" value={formData.website || ''} onChange={e => setFormData({
              ...formData,
              website: e.target.value
            })} data-id="element-2415" />
            </div>
            <div className="h-px bg-slate-100 my-4" data-id="element-2416"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2417">
              <Input label="Persona di riferimento" value={formData.contactPerson || ''} onChange={e => setFormData({
              ...formData,
              contactPerson: e.target.value
            })} data-id="element-2418" />
              <Input label="Cellulare referente" type="tel" value={formData.contactPhone || ''} onChange={e => setFormData({
              ...formData,
              contactPhone: e.target.value
            })} data-id="element-2419" />
            </div>
          </div>;
      case 4:
        // Dati Fiscali
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2420">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2421">
              <Input label="Partita IVA" value={formData.vatNumber || ''} onChange={e => setFormData({
              ...formData,
              vatNumber: e.target.value
            })} maxLength={11} data-id="element-2422" />
              <Input label="Codice fiscale" value={formData.fiscalCode || ''} onChange={e => setFormData({
              ...formData,
              fiscalCode: e.target.value
            })} maxLength={16} data-id="element-2423" />
              <Input label="Codice SDI" value={formData.sdiCode || ''} onChange={e => setFormData({
              ...formData,
              sdiCode: e.target.value
            })} maxLength={7} data-id="element-2424" />
              <Input label="Codice Ateco" value={formData.atecoCode || ''} onChange={e => setFormData({
              ...formData,
              atecoCode: e.target.value
            })} data-id="element-2425" />
            </div>
            <Textarea label="Attività aziendale" value={formData.activityDescription || ''} onChange={e => setFormData({
            ...formData,
            activityDescription: e.target.value
          })} data-id="element-2426" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2427">
              <Input label="Legale rappresentante" value={formData.legalRep || ''} onChange={e => setFormData({
              ...formData,
              legalRep: e.target.value
            })} data-id="element-2428" />
              <Input label="Datore di lavoro" value={formData.employer || ''} onChange={e => setFormData({
              ...formData,
              employer: e.target.value
            })} data-id="element-2429" />
            </div>
          </div>;
      case 5:
        // Relazioni
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2430">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2431">
              <Select label="Provenienza cliente" value={formData.source || ''} onChange={e => setFormData({
              ...formData,
              source: e.target.value
            })} options={[{
              value: 'Web',
              label: 'Web'
            }, {
              value: 'Passaparola diretto',
              label: 'Passaparola diretto'
            }, {
              value: 'Partner',
              label: 'Partner'
            }, {
              value: 'Commercialista',
              label: 'Commercialista'
            }, {
              value: 'Altro',
              label: 'Altro'
            }]} data-id="element-2432" />
              <Select label="Referente interno" value={formData.safetyReferent || ''} onChange={e => setFormData({
              ...formData,
              safetyReferent: e.target.value
            })} options={[{
              value: 'alessio',
              label: 'Alessio'
            }, {
              value: 'gianfranco',
              label: 'Gian Franco'
            }, {
              value: 'lorenzo',
              label: 'Lorenzo'
            }, {
              value: 'tiziano',
              label: 'Tiziano'
            }]} required disabled={!canEdit('clients', 'safetyReferent')} data-id="element-2433" />
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex justify-between items-center" data-id="element-2434">
              <div data-id="element-2435">
                <h4 className="font-bold text-seform-navy" data-id="element-2436">
                  Dipendenti censiti
                </h4>
                <p className="text-sm text-slate-500" data-id="element-2437">
                  Calcolato automaticamente dal modulo Lavoratori
                </p>
              </div>
              <div className="text-3xl font-bold text-seform-orange" data-id="element-2438">
                {formData.employeesCount || 0}
              </div>
            </div>
          </div>;
    }
  };
  return <Layout data-id="element-2439">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-2440">
        <div data-id="element-2441">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2442">
            Anagrafiche clienti
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-2443">
            Gestione completa delle aziende clienti e prospect.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-2444">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-2445">
            <button onClick={() => setViewMode('principale')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'principale' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2446">
              Principale
            </button>
            <button onClick={() => setViewMode('pending')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'pending' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2447">
              Pending
            </button>
          </div>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-2449" />} data-id="element-2448">
            Esporta
          </Button>
          <PermissionGate module="clients" action="create" data-id="element-2450">
            <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-2452" />} data-id="element-2451">
              Nuova anagrafica
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters & Search */}
      <Card className="mb-6" noPadding data-id="element-2453">
        <div className="p-4 flex flex-col gap-4" data-id="element-2454">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-2455">
            <div className="flex-1" data-id="element-2456">
              <Input placeholder="Cerca per ragione sociale, P.IVA, referente..." startContent={<Search className="h-4 w-4" data-id="element-2458" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-2457" />
            </div>
            <div className="flex gap-2" data-id="element-2459">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-2461" />} data-id="element-2460">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-2462">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="clients" lockedColumns={['name']} data-id="element-2463" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-2464">
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti gli stati'
          }, {
            value: 'active',
            label: 'Attivo'
          }, {
            value: 'prospect',
            label: 'Prospect'
          }, {
            value: 'inactive',
            label: 'Non attivo'
          }, {
            value: 'draft',
            label: 'Bozza'
          }]} data-id="element-2465" />
              <Select label="Zona" value={filters.zone || ''} onChange={e => setFilter('zone', e.target.value)} options={[{
            value: '',
            label: 'Tutte le zone'
          }, {
            value: 'arezzo',
            label: 'Arezzo'
          }, {
            value: 'valdarno',
            label: 'Valdarno'
          }, {
            value: 'casentino',
            label: 'Casentino'
          }, {
            value: 'firenze',
            label: 'Firenze'
          }]} data-id="element-2466" />
              <div className="col-span-full flex justify-end" data-id="element-2467">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-2469" />} className="font-medium" data-id="element-2468">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      {/* Data Table */}
      <Card noPadding className="overflow-hidden" data-id="element-2470">
        <div className="overflow-x-auto" data-id="element-2471">
          <table className="w-full text-left border-collapse table-auto" data-id="element-2472">
            <thead data-id="element-2473">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2474">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''} ${col.key === 'number' ? 'w-24' : ''}`} data-id="element-2475">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2476">
              {isSearching ? <tr data-id="element-2477">
                  <td colSpan={visibleColumns.length} className="px-4 py-12 text-center text-slate-500" data-id="element-2478">
                    <div className="flex justify-center items-center gap-2" data-id="element-2479">
                      <div className="animate-spin h-5 w-5 border-2 border-seform-orange border-t-transparent rounded-full" data-id="element-2480"></div>
                      Ricerca in corso...
                    </div>
                  </td>
                </tr> : paginatedData.length > 0 ? paginatedData.map(client => <tr key={client.id} className="hover:bg-slate-50/80 transition-colors group cursor-pointer" onClick={() => navigate(`/clients/${client.id}`)} data-id="element-2481">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 text-sm font-mono text-slate-500" data-id="element-2482">
                        {client.number}
                      </td>}
                    {visibleColumns.includes('name') && <td className="px-4 py-4" data-id="element-2483">
                        <div data-id="element-2484">
                          <p className="text-xs font-semibold text-slate-900" data-id="element-2485">
                            {client.name}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-2486">
                            {client.email}
                          </p>
                        </div>
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2487">
                        <div className="flex items-center gap-2" data-id="element-2488">
                          {viewMode === 'pending' ? <Badge variant="warning" data-id="element-2489">Pending</Badge> : <Badge variant={client.status === 'Attivo' ? 'success' : client.status === 'Prospect' ? 'info' : 'neutral'} data-id="element-2490">
                              {client.status}
                            </Badge>}
                        </div>
                      </td>}
                    {visibleColumns.includes('zone') && <td className="px-4 py-4" data-id="element-2491">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-2492">
                          <MapPin className="h-3.5 w-3.5 text-slate-400" data-id="element-2493" />
                          {client.zone}
                        </div>
                      </td>}
                    {visibleColumns.includes('clientOf') && <td className="px-4 py-4" data-id="element-2494">
                        <div className="flex gap-1 flex-wrap" data-id="element-2495">
                          {client.clientOf?.map((s: string) => <span key={s} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200 capitalize" data-id="element-2496">
                              {s.replace('_', ' ')}
                            </span>)}
                        </div>
                      </td>}
                    {visibleColumns.includes('safetyReferent') && <td className="px-4 py-4 text-sm text-slate-600 capitalize" data-id="element-2497">
                        {client.safetyReferent}
                      </td>}
                    {visibleColumns.includes('employeesCount') && <td className="px-4 py-4 text-sm text-slate-600 font-mono" data-id="element-2498">
                        {client.employeesCount}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" onClick={e => e.stopPropagation()} data-id="element-2499">
                        <div className="flex items-center gap-1" data-id="element-2500">
                          <Tooltip content="Visualizza cliente" data-id="element-2501">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => navigate(`/clients/${client.id}${viewMode === 'pending' ? '?pending=true' : ''}`)} data-id="element-2502">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-2503" />
                            </Button>
                          </Tooltip>
                          <PermissionGate module="clients" action="edit" data-id="element-2504">
                            <Tooltip content="Modifica cliente" data-id="element-2505">
                              <Button variant="light" size="sm" className="!px-1" onClick={() => navigate(`/clients/${client.id}?edit=true${viewMode === 'pending' ? '&pending=true' : ''}`)} data-id="element-2506">
                                <Edit className="h-4 w-4 text-seform-orange" data-id="element-2507" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate module="clients" action="delete" data-id="element-2508">
                            <Tooltip content="Elimina cliente" data-id="element-2509">
                              <Button variant="light" size="sm" className="!px-1" onClick={e => handleDeleteClick(e, client)} data-id="element-2510">
                                <Trash2 className="h-4 w-4 text-red-500" data-id="element-2511" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                        </div>
                      </td>}
                  </tr>) : <tr data-id="element-2512">
                  <td colSpan={visibleColumns.length} className="px-4 py-12 text-center text-slate-500" data-id="element-2513">
                    Nessun cliente trovato
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-100" data-id="element-2514">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2515" />
        </div>
      </Card>

      {/* Create/Edit Wizard Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingClient ? 'Modifica anagrafica' : 'Nuova anagrafica'} size="2xl" data-id="element-2516">
        <AnimatePresence mode="wait" data-id="element-2517">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2518">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2519">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2520" />
              </motion.div>
              <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg font-semibold text-slate-800" data-id="element-2521">
                Anagrafica salvata con successo
              </motion.p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col h-full" data-id="element-2522">
              {/* Stepper */}
              <div className="mb-8 px-2" data-id="element-2523">
                <div className="flex items-center justify-between relative" data-id="element-2524">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-100 -z-10" data-id="element-2525"></div>
                  {[1, 2, 3, 4, 5].map(step => <div key={step} className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors
                        ${step === currentStep ? 'bg-seform-orange text-white shadow-lg scale-110' : step < currentStep ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'}
                      `} data-id="element-2526">
                      {step < currentStep ? <CheckCircle2 className="h-5 w-5" data-id="element-2527" /> : step}
                    </div>)}
                </div>
                <div className="flex justify-between mt-2 text-xs font-medium text-slate-500 px-1" data-id="element-2528">
                  <span data-id="element-2529">Dati base</span>
                  <span data-id="element-2530">Indirizzi</span>
                  <span data-id="element-2531">Contatti</span>
                  <span data-id="element-2532">Fiscali</span>
                  <span data-id="element-2533">Relazioni</span>
                </div>
              </div>

              {/* Step Content */}
              <div className="flex-1 overflow-y-auto custom-scrollbar px-1 py-2" data-id="element-2534">
                {renderWizardStep()}
              </div>

              {/* Footer Actions */}
              <div className="flex justify-between items-center pt-6 mt-6 border-t border-slate-100" data-id="element-2535">
                <Button variant="flat" color="default" onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : setIsModalOpen(false)} startContent={currentStep > 1 ? <ChevronLeft className="h-4 w-4" data-id="element-2537" /> : undefined} data-id="element-2536">
                  {currentStep > 1 ? 'Indietro' : 'Annulla'}
                </Button>

                <div className="flex gap-3" data-id="element-2538">
                  <Button variant="light" size="sm" className="text-slate-400" data-id="element-2539">
                    Salva bozza
                  </Button>
                  {currentStep < 5 ? <Button onClick={() => setCurrentStep(currentStep + 1)} endContent={<ChevronRight className="h-4 w-4" data-id="element-2541" />} data-id="element-2540">
                      Avanti
                    </Button> : <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2543" />} color="success" data-id="element-2542">
                      Completa e salva
                    </Button>}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-2544">
        <AnimatePresence mode="wait" data-id="element-2545">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-2546">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2547">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-2548" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2549">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2550">
              <div className="space-y-6" data-id="element-2551">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-2552">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-2553" />
                  <div data-id="element-2554">
                    <p className="text-sm text-red-700 font-medium" data-id="element-2555">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-2556">
                      L'anagrafica <strong data-id="element-2557">{clientToDelete?.name}</strong> verrà
                      eliminata definitivamente insieme a tutti i dati
                      associati.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-2558">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-2559">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={handleConfirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-2561" />} data-id="element-2560">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}